import { useQuery } from '@tanstack/react-query';
import { Button, Modal, Skeleton, Typography } from '@volvo/vcdk-react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import { getWifiErrorHistoryQueryOptions } from '@/service/vpc/queryOptions';

type Props = {
  chassisId: string;
  isOpen: boolean;
  onClose: () => void;
};

const WifiErrorLogModal: React.FC<Props> = ({ chassisId, isOpen, onClose }) => {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    ...getWifiErrorHistoryQueryOptions({ chassisId }),
    enabled: isOpen,
  });

  const errors = data?.wifiErrorHistoryResponse?.errors ?? [];

  if (!isOpen) return null;

  return createPortal(
    <Modal open={isOpen} onClose={onClose} maxWidth="90%" width={700}>
      <span slot="header">{t(($) => $.components.modal.wifi_error_log_modal.title)}</span>
      <div className="flex max-h-96 flex-col gap-2 overflow-y-auto">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="rounded-rectangle" width="100%" height="20px" />
          ))
        ) : errors.length === 0 ? (
          <Typography variant="body">{t(($) => $.components.modal.wifi_error_log_modal.no_errors)}</Typography>
        ) : (
          errors.map((entry, i) => (
            <Typography key={i} variant="body">
              {`[${entry.receivedDateTime}, ${entry.errorType}] ${entry.errorMessage}`}
            </Typography>
          ))
        )}
      </div>
      <span slot="modalFooter">
        <Button variant="primary" onClick={onClose}>
          {t(($) => $.components.modal.wifi_error_log_modal.close)}
        </Button>
      </span>
    </Modal>,
    document.body
  );
};

export default withComponentErrorBoundary(WifiErrorLogModal);
